import React, { useState } from 'react';
import { Box, Paper, Typography, CircularProgress, FormControl, InputLabel, Select, MenuItem, SelectChangeEvent, useTheme, useMediaQuery } from '@mui/material';
import { Bar } from 'react-chartjs-2';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface MonthlyDataItem {
  month: string;
  total?: number;
  count?: number;
  amount?: number; // Para dados de despesas
}

interface MonthlyBarChartProps {
  title: string;
  data: MonthlyDataItem[];
  loading?: boolean;
  error?: string | null;
  height?: number | string;
  color?: string;
  valueLabel?: string;
  embedded?: boolean;
}

const MonthlyBarChart: React.FC<MonthlyBarChartProps> = ({
  title,
  data,
  loading = false,
  error = null,
  height = 350,
  color = '#4CAF50',
  valueLabel = 'Vendas (R$)',
  embedded = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const adjustedHeight = embedded
    ? height
    : isMobile
      ? typeof height === 'number'
        ? Math.max(height, 450)
        : '450px'
      : height;

  const Wrapper: React.ElementType = embedded ? Box : Paper;
  const paperProps = embedded ? {} : { elevation: 3 };
  const wrapperSx = embedded
    ? { height: adjustedHeight, minHeight: typeof adjustedHeight === 'number' ? adjustedHeight : undefined }
    : { p: 2, height: adjustedHeight, minHeight: isMobile ? '450px' : '350px' };
  
  // Extrair todos os anos disponíveis nos dados
  const getAvailableYears = () => {
    if (!data || data.length === 0) return [new Date().getFullYear()];
    
    const years = data.map(item => {
      const year = item.month.split('-')[0];
      return parseInt(year);
    });
    
    // Remover duplicatas e ordenar
    const uniqueYears = Array.from(new Set(years));
    return uniqueYears.sort((a, b) => b - a); // Ordenação decrescente
  };

  const availableYears = getAvailableYears();
  const [selectedYear, setSelectedYear] = useState<number>(
    availableYears.length > 0 ? availableYears[0] : new Date().getFullYear()
  );

  const handleYearChange = (event: SelectChangeEvent<number>) => {
    setSelectedYear(Number(event.target.value));
  };

  // Filtrar dados pelo ano selecionado
  const filterDataByYear = (items: MonthlyDataItem[], year: number) => {
    return items.filter(item => {
      const itemYear = parseInt(item.month.split('-')[0]);
      return itemYear === year;
    });
  };

  // Ordenar os meses cronologicamente
  const sortByMonth = (items: MonthlyDataItem[]) => {
    return [...items].sort((a, b) => {
      // Meses já estão em formato YYYY-MM, então podemos comparar diretamente
      return a.month.localeCompare(b.month);
    });
  };

  // Formatar nome do mês para exibição
  const formatMonth = (dateString: string) => {
    try {
      const [year, month] = dateString.split('-');
      const date = new Date(parseInt(year), parseInt(month) - 1, 1);
      return format(date, 'MMM', { locale: ptBR });
    } catch (error) {
      console.error('Erro ao formatar mês:', dateString, error);
      return '-';
    }
  };

  // Obter o valor numérico do item (total ou amount)
  const getItemValue = (item: MonthlyDataItem): number => {
    if (item.total !== undefined) return item.total;
    if (item.amount !== undefined) return item.amount;
    return 0;
  };

  // Filtrar e processar dados para o gráfico
  const processChartData = () => {
    if (!data || data.length === 0) {
      return {
        labels: [],
        datasets: [
          {
            label: valueLabel,
            data: [],
            backgroundColor: color,
            borderRadius: 5,
          },
        ],
      };
    }

    const filteredData = filterDataByYear(data, selectedYear);
    const sortedData = sortByMonth(filteredData);
    
    // Se não houver dados para o ano selecionado, retornar array vazio
    if (sortedData.length === 0) {
      return {
        labels: [],
        datasets: [
          {
            label: valueLabel,
            data: [],
            backgroundColor: color,
            borderRadius: 5,
          },
        ],
      };
    }

    return {
      labels: sortedData.map(item => formatMonth(item.month)),
      datasets: [
        {
          label: valueLabel,
          data: sortedData.map(item => getItemValue(item)),
          backgroundColor: color,
          borderRadius: 5,
        },
      ],
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          boxWidth: isMobile ? 12 : 15,
          font: {
            size: isMobile ? 12 : 14
          }
        }
      },
      title: {
        display: false,
      },
      // Adicionar informações de valor no tooltip
      tooltip: {
        callbacks: {
          label: function(context: any) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += `R$ ${context.parsed.y.toLocaleString('pt-BR')}`;
            }
            return label;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value: any) {
            return `R$ ${value.toLocaleString('pt-BR')}`;
          },
          font: {
            size: isMobile ? 10 : 12
          }
        }
      },
      x: {
        ticks: {
          font: {
            size: isMobile ? 10 : 12
          }
        }
      }
    },
  };

  const headerOffset = embedded ? 48 : 70;

  // Renderizar carregamento
  if (loading) {
    return (
      <Wrapper {...paperProps} sx={wrapperSx}>
        {!embedded && title ? <Typography variant="h6" gutterBottom>{title}</Typography> : null}
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: `calc(100% - ${embedded ? 0 : 40}px)` }}>
          <CircularProgress />
        </Box>
      </Wrapper>
    );
  }

  // Renderizar mensagem de erro
  if (error) {
    return (
      <Wrapper {...paperProps} sx={wrapperSx}>
        {!embedded && title ? <Typography variant="h6" gutterBottom>{title}</Typography> : null}
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: `calc(100% - ${embedded ? 0 : 40}px)` }}>
          <Typography color="error">{error}</Typography>
        </Box>
      </Wrapper>
    );
  }

  const chartData = processChartData();
  const hasData = chartData.datasets[0].data.length > 0;

  return (
    <Wrapper {...paperProps} sx={wrapperSx}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: embedded ? 1 : 2, px: embedded ? 1 : 0 }}>
        {!embedded && title ? <Typography variant="h6">{title}</Typography> : <Box />}
        <FormControl size="small" sx={{ minWidth: 100, ml: 'auto' }}>
          <InputLabel id="year-select-label">Ano</InputLabel>
          <Select
            labelId="year-select-label"
            id="year-select"
            value={selectedYear}
            label="Ano"
            onChange={handleYearChange}
          >
            {availableYears.map(year => (
              <MenuItem key={year} value={year}>{year}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      
      <Box sx={{ height: `calc(100% - ${headerOffset}px)`, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        {hasData ? (
          <Bar data={chartData} options={chartOptions} />
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Typography color="text.secondary">Sem dados disponíveis para {selectedYear}</Typography>
          </Box>
        )}
      </Box>
    </Wrapper>
  );
};

export default MonthlyBarChart; 